import { Know } from 'interfaces/Know'
import theme from 'stories/utils/theme'
import HomepageKnowCard from 'stories/V1/components/HomepageKnowCard'
import SectionContainer from 'stories/V1/layouts/SectionContainer'
import styled from 'styled-components'
import { useRef } from 'react'
import _useTranslation from 'lib/hooks/_useTranslation'
import { NewButtonLink } from 'components/elements/NewButton'
import { ButtonText } from 'stories/elements/Typography/Text'
import { seoSubjectsRoute } from 'lib/constants/routes'
import { AnalyticsEventHomepageSubjectsClick } from 'lib/constants/AnalyticsEventType'
import { trackEvent } from 'lib/tracking/analytics-service'

const SectionWrapper = styled.div`
    width: 100%;
    overflow: hidden;
    max-width: 1024px;
    @media (max-width: 1050px) {
        padding: 0;
        max-width: unset;
        width: 100vw;
        max-width: 100vw;
    }
`

const Wrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    justify-items: center;
    align-items: center;
    width: 100%;
    gap: 10px;

    @media (max-width: 1150px) {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }

    @media (max-width: 750px) {
        all: unset;
        display: flex;
        flex-wrap: nowrap;
        overflow-x: scroll;
        overflow-y: hidden;
        width: 100%;
        padding: 0 24px;
        gap: 10px;
        -ms-overflow-style: none;
        scrollbar-width: none;
        margin-right: 24px;
        -webkit-overflow-scrolling: touch;
        touch-action: pan-x pan-y;

        &::after {
            content: '';
            flex: 0 0 42px;
            min-width: 42px;
        }

        &::-webkit-scrollbar {
            display: none;
        }
    }
`

const CardWrapper = styled.div`
    transition: opacity 0.3s ease-in-out;
    animation: fadeIn 0.4s ease-in-out;
    display: flex;
    justify-content: center;

    h3 {
        font-family: var(--font-inter);
        font-variation-settings: 'opsz' 32 !important;
        font-feature-settings: 'calt', 'cv01', 'cv02', 'cv03', 'cv04', 'cv05', 'cv08', 'cv09';
        font-size: 17px;
        line-height: 20px;
        letter-spacing: 0.3px;
        font-weight: 650;
    }

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    p {
        font-family: var(--font-inter);
        font-variation-settings: 'opsz' 32 !important;
        font-feature-settings: 'calt', 'cv01', 'cv02', 'cv03', 'cv04', 'cv05', 'cv08', 'cv09';
        font-size: 17px;
        line-height: 20px;
        letter-spacing: 0.3px;
        font-weight: 650;
    }
`

interface Props {
    knows: Know[]
    onSimilarKnowsClicked: (know: Know) => void
    isMobile?: boolean
}

const SimilarKnows = ({ knows = [], onSimilarKnowsClicked, isMobile }: Props) => {
    const wrapperRef = useRef<HTMLDivElement>(null)
    const { t } = _useTranslation('knows')
    const { t: tHomepage } = _useTranslation('homepage')

    return (
        <SectionContainer
            backgroundColor="#14183a"
            title={t('knows/similarContentHeading')}
            borderRadius="48px"
            fullWidth
            mobileBorderRadius="32px"
            noVerticalPadding={isMobile}
        >
            <>
                <SectionWrapper>
                    <Wrapper ref={wrapperRef}>
                        {knows?.length
                            ? knows?.slice(0, 3).map((know) => (
                                  <CardWrapper key={know.slug} onClick={() => onSimilarKnowsClicked(know)}>
                                      <HomepageKnowCard
                                          knowUuid={know.slug}
                                          know={know}
                                          properties={{ isLarge: !isMobile }}
                                      />
                                  </CardWrapper>
                              ))
                            : null}
                    </Wrapper>
                </SectionWrapper>
                <ButtonWrapper>
                    <span
                        onClick={() => {
                            trackEvent(AnalyticsEventHomepageSubjectsClick, { referrerScreen: 'homepage' })
                        }}
                    >
                        <NewButtonLink
                            color={theme.colors.white}
                            borderRadius={theme.borderRadius.webappButton}
                            width={200}
                            height={44}
                            href={seoSubjectsRoute}
                        >
                            <ButtonText color={theme.colors.headlineDark}>
                                {tHomepage('homepage/moreSubjectButtonText')}
                            </ButtonText>
                        </NewButtonLink>
                    </span>
                </ButtonWrapper>
            </>
        </SectionContainer>
    )
}

export default SimilarKnows
